import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Skeleton } from 'antd'
import { PQueryBuilderProvider } from '@pushly/aqe'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder/pquery-builder'
import { useService } from '@pushly/aqe/lib/hooks'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import type { SegmentQueryField, SegmentQueryFieldSurface } from '@pushly/models/lib/interfaces/segment-query-field'
import type { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import type { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import { SegmentQueryFieldsService } from '../../../services/segment-query-fields.service'
import { ExternalEventTypeService } from '../../../services/external-event-type.service'
import { useSegmentQueryFields } from '../../segment-query-builder/use-segment-query-fields'
import {
    SEGMENT_VALUE_INPUTS,
    SEGMENT_VALUE_VALIDATORS,
    useSegmentValueSummaries,
} from '../../segment-query-builder/value-inputs'
import { SegmentValueInputProvider } from '../../segment-query-builder/value-inputs/segment-value-input-provider'
import { useSegmentValueInputContext } from '../../segment-query-builder/value-inputs/segment-value-input-context'
import { JOURNEY_QUERY_CONSTRAINTS } from '../../../features/journeys/journey-query-constraints'
import { buildEventPropertyFields, isEventPropertyReference } from '../../../features/journeys/external-events/event-property-fields'
import {
    EVENT_BINDABLE_INPUT_KEY,
    withEventBindableValueInputs,
} from '../../../features/journeys/external-events/event-bindable-fields'
import { createEventBindingSummary } from '../../../features/journeys/external-events/event-bindable-value-input'
import {
    EVENT_CONDITION_VALUE_INPUTS,
    EVENT_CONDITION_VALUE_VALIDATORS,
} from '../../../features/journeys/external-events/event-condition-builder'
import { EventPropertyProvider } from '../../../features/journeys/external-events/event-property-context'
import { findRegisteredEventType } from '../../../features/journeys/external-events/event-type-selection'

const NO_CATALOG: SegmentQueryField[] = []
const NO_EVENT_PROPERTIES: PQueryBuilderField[] = []

/**
 * The document to open the canvas on.
 *
 * A journey stores `{}` from the moment entry criteria are switched on until the first condition
 * is saved, and the builder reads a document with no condition tree as one it cannot represent —
 * so opening straight onto a stored `{}` would report the criteria as unsupported and refuse to
 * edit a journey that simply has none yet.
 *
 * The group is an `or`, so conditions added inside it read as alternatives, and groups still
 * combine with AND at the root. Safe to seed because a group that stays empty is dropped rather
 * than stored, so the scaffolding never reaches the wire.
 */
function openableDocument(value: PQueryDocument | undefined): Record<string, unknown> {
    const stored = (value ?? {}) as Record<string, unknown>

    return Object.keys(stored).length ? stored : { and: [{ or: [] }] }
}

export interface JourneyEntryCriteriaProps {
    domainId: number
    /** The stored criteria. An absent or empty document opens an empty canvas. */
    value: PQueryDocument | undefined
    onChange: (criteria: PQueryDocument) => void
    disabled?: boolean
    /**
     * The event type a value may be bound to instead of held as a literal.
     *
     * Absent for every trigger that enters subscribers on their own account, which is what leaves
     * the criteria a plain subscriber query. Naming one adds the event's registered properties as
     * a second value source and changes nothing else about the surface.
     */
    eventName?: string
    schemaVersion?: number
    /**
     * Which of the catalog's fields to offer, named for whatever evaluates the criteria.
     *
     * A trigger whose entering event carries only a few properties is checked in memory against that
     * event, so it may only be offered what the event holds — the default. A trigger whose criteria
     * become a query over subscriber records can be offered everything a segment can, custom
     * attributes included, so it names the segment surface. Offering a field the evaluator cannot
     * read builds a condition that matches nothing rather than one that fails.
     */
    fieldSurface?: SegmentQueryFieldSurface
    /**
     * The delivery channels in play. A field that applies to particular channels is offered only when
     * one of them is; absent means the channel has not been chosen and nothing is filtered.
     */
    channels?: DeliveryChannel[]
    /** Substitutes for the catalog read. Must keep its identity for as long as this is mounted. */
    fieldsService?: Pick<SegmentQueryFieldsService, 'getDomainFieldsCached'>
    /** Substitutes for the registry read. Must keep its identity for as long as this is mounted. */
    eventTypeService?: Pick<ExternalEventTypeService, 'list'>
}

/**
 * The conditions a subscriber must satisfy to enter a journey.
 *
 * The same builder the segment screen uses, over the same catalog, narrowed to the fields the
 * catalog says entry criteria may be built from. Entry criteria are the same properties with the
 * same operators — what differs is the evaluator — so nothing about the vocabulary is restated
 * here. A field becomes available to journeys by being marked in the catalog, not by a change to
 * this component.
 *
 * One component serves every trigger, including the one entered by an external event. A trigger
 * that names an event type differs only in where a condition's value may come from: the same
 * fields, the same operators, the same vocabularies and the same layout, with the event's
 * registered properties offered alongside a literal wherever the catalog marks a field bindable.
 * Keeping that a second value source rather than a second component is what stops the two surfaces
 * drifting into two different ways of writing the same query.
 *
 * The document this reads and writes is the one the entry evaluator already consumes.
 */
export function JourneyEntryCriteria({
    domainId,
    value,
    onChange,
    disabled,
    eventName,
    schemaVersion,
    fieldSurface = 'journey_entry',
    channels,
    fieldsService,
    eventTypeService,
}: JourneyEntryCriteriaProps) {
    const resolvedFieldsSvc = useService(SegmentQueryFieldsService)
    const fieldsSvc = fieldsService ?? resolvedFieldsSvc
    const resolvedEventTypeSvc = useService(ExternalEventTypeService)
    const eventTypeSvc = eventTypeService ?? resolvedEventTypeSvc

    const [catalog, setCatalog] = useState<SegmentQueryField[] | null>(null)
    const [eventProperties, setEventProperties] = useState<PQueryBuilderField[]>(NO_EVENT_PROPERTIES)

    useEffect(() => {
        let live = true

        // A failed read settles as an empty catalog rather than staying null, so this never waits
        // forever — the criteria render with nothing to choose from, which is visible, instead of
        // a skeleton that never resolves.
        fieldsSvc
            .getDomainFieldsCached(domainId)
            .then((fields) => live && setCatalog(fields))
            .catch(() => live && setCatalog(NO_CATALOG))

        return () => {
            live = false
        }
    }, [fieldsSvc, domainId])

    useEffect(() => {
        if (!eventName) {
            setEventProperties(NO_EVENT_PROPERTIES)
            return
        }

        let live = true

        // Read independently of the catalog so one failing does not cost the other. A registry that
        // cannot be read leaves the subscriber conditions fully authorable with nothing to bind to,
        // which is the degraded surface worth having; the service reports its own failure.
        eventTypeSvc
            .list(domainId)
            .then((response) => {
                if (!live) return
                const registered = response.ok && Array.isArray(response.data) ? response.data : []
                setEventProperties(
                    buildEventPropertyFields(findRegisteredEventType(registered, eventName, schemaVersion)?.properties),
                )
            })
            .catch(() => live && setEventProperties(NO_EVENT_PROPERTIES))

        return () => {
            live = false
        }
    }, [eventTypeSvc, domainId, eventName, schemaVersion])

    const fields = useSegmentQueryFields(catalog ?? NO_CATALOG, { surface: fieldSurface, channels })

    // Which fields may hold an event reference is decided on the catalog and read by the builder
    // through the key it already resolves editors by. Returned by identity when no event type is
    // named, so a journey with an ordinary trigger hands the builder exactly what the catalog
    // served.
    const authorableFields = useMemo(
        () => (eventName ? withEventBindableValueInputs(fields) : fields),
        [fields, eventName],
    )

    // The document as it currently stands, for the renderers that need to read the rest of the
    // query rather than only their own condition. Held in a ref because they read it when the
    // reader opens a row, not when it changes.
    const document = useRef<Record<string, unknown>>(openableDocument(value))
    const readDocument = useCallback(() => document.current, [])

    const handleRuleCommit = useCallback(
        (next: Record<string, unknown>) => {
            document.current = next
            onChange(next as PQueryDocument)
        },
        [onChange],
    )

    // The builder cannot offer a property picker before it knows the properties, and mounting it
    // early would give it the document to parse against an empty catalog — every condition would
    // read as one on a field that does not exist.
    if (catalog === null) {
        return <Skeleton active={true} title={false} paragraph={{ rows: 2 }} />
    }

    // A catalog that offers this surface nothing is a server that has not been told about it yet.
    // Rendering the builder anyway would be an empty property picker with no explanation, and
    // saying so is what keeps a deploy in the wrong order from reading as a broken screen.
    if (fields.length === 0) {
        return (
            <Alert
                type="info"
                title="Entry criteria are unavailable for this domain."
                description="No subscriber attributes are currently offered for journey entry. Any criteria already saved on this journey are unchanged."
            />
        )
    }

    // The vocabularies are decided from the catalog as served: the fields that carry a bindable
    // editor are the ones whose own control this replaces, and none of them is a control the
    // provider loads a list for.
    return (
        <SegmentValueInputProvider level="domain" domainId={domainId} fields={fields} getDocument={readDocument}>
            <EventPropertyProvider properties={eventProperties}>
                <JourneyEntryCriteriaStore
                    value={document.current}
                    fields={authorableFields}
                    eventProperties={eventProperties}
                    bindable={!!eventName}
                    disabled={disabled}
                    onRuleCommit={handleRuleCommit}
                />
            </EventPropertyProvider>
        </SegmentValueInputProvider>
    )
}

interface StoreProps {
    value: Record<string, unknown>
    fields: PQueryBuilderField[]
    eventProperties: PQueryBuilderField[]
    /** Whether the registries offering an event reference as a value are in play. */
    bindable: boolean
    disabled?: boolean
    onRuleCommit: (next: Record<string, unknown>) => void
}

/**
 * The query store, standing inside the value context rather than around it.
 *
 * A row summary is a plain function the builder calls with a condition, so anything it needs from
 * outside that condition — a prompt's name, for one — has to be closed over when the registry of
 * summaries is built. Building it here is what lets a summary re-read as the vocabularies arrive
 * instead of naming an id for the whole session.
 */
function JourneyEntryCriteriaStore({ value, fields, eventProperties, bindable, disabled, onRuleCommit }: StoreProps) {
    const segmentSummaries = useSegmentValueSummaries()

    // Rebuilt when the registry lands, because a summary reads a property's name out of it — a row
    // collapsed before the event type resolved would otherwise keep reading the raw path forever.
    const valueSummaries = useMemo(
        () =>
            bindable
                ? { ...segmentSummaries, [EVENT_BINDABLE_INPUT_KEY]: createEventBindingSummary(eventProperties, segmentSummaries) }
                : segmentSummaries,
        [bindable, segmentSummaries, eventProperties],
    )

    // The same read the segment builder hands the builder, and correct here for the same reason:
    // the catalog names these properties, so a field whose values are only partly carried inline
    // is searched at the source under the name the store holds them by.
    const { loadFieldValues } = useSegmentValueInputContext()

    return (
        <PQueryBuilderProvider
            value={value}
            fields={fields}
            valueInputs={bindable ? EVENT_CONDITION_VALUE_INPUTS : SEGMENT_VALUE_INPUTS}
            valueSummaries={valueSummaries}
            valueValidators={bindable ? EVENT_CONDITION_VALUE_VALIDATORS : SEGMENT_VALUE_VALIDATORS}
            isValueReference={bindable ? isEventPropertyReference : undefined}
            loadFieldValues={loadFieldValues}
            constraints={JOURNEY_QUERY_CONSTRAINTS}
            onRuleCommit={onRuleCommit}
        >
            <PQueryBuilder disabled={disabled} />
        </PQueryBuilderProvider>
    )
}
